import { GAME_CONSTANTS } from '../utils/Constants';
import { WaveData, EventType, EnemyType } from '../types/GameTypes';
import { calculateEnemyCount } from '../types/EnemyTypes';

export class WaveSystem {
  private currentWave: number = 0;
  private isSurvivalMode: boolean = false;

  getCurrentWave(): number {
    return this.currentWave;
  }

  isEventWave(wave: number): boolean {
    // Event waves are 2 waves before each boss
    const nextBossWave = Math.ceil(wave / GAME_CONSTANTS.BOSS_WAVE_INTERVAL) * GAME_CONSTANTS.BOSS_WAVE_INTERVAL;
    return wave === nextBossWave - GAME_CONSTANTS.EVENT_WAVE_OFFSET;
  }

  isBossWave(wave: number): boolean {
    return wave % GAME_CONSTANTS.BOSS_WAVE_INTERVAL === 0;
  }

  isMinibossWave(wave: number): boolean {
    return (
      wave >= GAME_CONSTANTS.MINIBOSS_START_WAVE &&
      wave % GAME_CONSTANTS.MINIBOSS_WAVE_INTERVAL === 0 &&
      !this.isBossWave(wave)
    );
  }

  getEventType(wave: number): EventType | null {
    if (!this.isEventWave(wave)) {
      return null;
    }

    // Event types based on wave progression
    if (wave <= 20) {
      return 'runner_swarm';
    } else if (wave <= 40) {
      const events: EventType[] = ['ghost_surge', 'aerial_swarm', 'armor_push'];
      return events[Math.floor(Math.random() * events.length)];
    } else if (wave <= 60) {
      const events: EventType[] = ['splitter_storm', 'healer_convoy', 'armor_push'];
      return events[Math.floor(Math.random() * events.length)];
    } else {
      // Late game: mixed chaos or any event
      const events: EventType[] = [
        'runner_swarm',
        'ghost_surge',
        'aerial_swarm',
        'armor_push',
        'splitter_storm',
        'healer_convoy',
        'mixed_chaos',
      ];
      return events[Math.floor(Math.random() * events.length)];
    }
  }

  generateWave(waveNumber: number, isSurvival: boolean = false): WaveData {
    this.currentWave = waveNumber;
    this.isSurvivalMode = isSurvival;

    const isEvent = this.isEventWave(waveNumber);
    const isBoss = this.isBossWave(waveNumber);
    const isMiniboss = this.isMinibossWave(waveNumber);
    const eventType = isEvent ? this.getEventType(waveNumber) : undefined;

    let enemyCount = calculateEnemyCount(waveNumber, isSurvival);
    let enemyTypes: EnemyType[] = [];

    if (isBoss) {
      // Boss wave
      enemyTypes = ['boss'];
      enemyCount = 1; // Boss + escorts handled separately
    } else if (isMiniboss) {
      // Miniboss wave
      enemyTypes = ['miniboss'];
      enemyCount = 1;
    } else if (isEvent && eventType) {
      // Event wave
      enemyTypes = this.getEventEnemyTypes(eventType, waveNumber);
    } else {
      // Standard wave
      enemyTypes = this.getStandardEnemyTypes(waveNumber);
    }

    // Reduce flying/ghost spawn by 50%
    enemyTypes = this.applyFlyingGhostReduction(enemyTypes);

    return {
      waveNumber,
      enemyCount,
      enemyTypes,
      isEvent: isEvent || false,
      eventType: eventType ?? undefined,
      isBoss,
      isMiniboss,
      bossEscorts: isBoss ? Math.min(10 + Math.floor(waveNumber / 10), 30) : undefined,
    };
  }
  
  /**
   * Reduce flying and ghost enemy spawns by 50%
   */
  private applyFlyingGhostReduction(enemyTypes: EnemyType[]): EnemyType[] {
    const reducedTypes: EnemyType[] = [];
    
    for (let i = 0; i < enemyTypes.length; i++) {
      const type = enemyTypes[i];
      // Note: Flying and Ghost enemy types are not implemented
      // This code is kept for future implementation
      if (false && (type === 'flying' || type === 'ghost')) {
        if (Math.random() < 0.5) {
          reducedTypes.push(type);
        }
      } else {
        reducedTypes.push(type);
      }
    }
    
    return reducedTypes;
  }

  private getStandardEnemyTypes(_wave: number): EnemyType[] {
    const types: EnemyType[] = ['ground'];

    // Add variety based on wave
    if (_wave >= 10) {
      types.push('runner');
    }
    if (_wave >= 15) {
      types.push('armored');
    }
    if (_wave >= 20) {
      types.push('splitter');
    }
    if (_wave >= 30) {
      types.push('healer');
    }
    if (_wave >= 25) {
      types.push('elite');
    }

    return types;
  }

  private getEventEnemyTypes(eventType: EventType, wave: number): EnemyType[] {
    switch (eventType) {
      case 'runner_swarm':
        return ['runner', 'runner', 'runner', 'splitter']; // 90% runners, 10% splitters
      case 'ghost_surge':
        return ['ghost', 'ghost', 'ghost', 'elite']; // 70% ghosts, 30% elites
      case 'aerial_swarm':
        return ['flying']; // 100% flying
      case 'armor_push':
        return ['armored', 'armored', 'armored', 'healer', 'elite']; // 60% armored, 20% healers, 20% elites
      case 'splitter_storm':
        return ['splitter']; // 100% splitters
      case 'healer_convoy':
        return ['healer', 'healer', 'healer', 'armored', 'elite']; // 60% healers, 40% escorts
      case 'mixed_chaos':
        return ['runner', 'ghost', 'flying', 'splitter', 'healer', 'armored', 'elite'];
      default:
        return ['ground'];
    }
  }

  startNextWave(): number {
    this.currentWave++;
    return this.currentWave;
  }

  enterSurvivalMode(): void {
    this.isSurvivalMode = true;
  }

  isInSurvivalMode(): boolean {
    return this.isSurvivalMode;
  }

  setBaseWave(wave: number): void {
    this.currentWave = wave;
  }
}

